import type { ReleaseInput, ReleaseDecision, SuggestedTestCase, ReleaseStatus } from './types';
import * as Sentry from '@sentry/react';

// 환경별 API Base URL 설정
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// API 에러 캡처 헬퍼
function captureApiError(error: Error, context: { endpoint: string; method: string; extra?: Record<string, unknown> }) {
  Sentry.captureException(error, {
    tags: {
      type: 'api_error',
      endpoint: context.endpoint,
      method: context.method,
    },
    extra: context.extra,
  });
}

// 현재 환경 정보
export const APP_ENV = import.meta.env.VITE_APP_ENV || 'local';

// ===== Quota Types =====
export interface QuotaInfo {
  plan: 'free' | 'pro' | 'max' | 'team';
  actual_plan?: 'free' | 'pro' | 'max' | 'team';  // 본인 실제 플랜
  monthly_evaluations: {
    used: number;
    limit: number;
    unlimited: boolean;
  };
  limits: {
    max_test_cases: number;
    max_planning_chars: number;
    history_limit: number;
    pdf_download: boolean;
    api_access: boolean;
    max_testcase_files: number;   // 테스트케이스 파일 동시 처리 개수
    max_planning_files: number;   // 기획서 파일 동시 처리 개수
    personal_trend?: boolean;     // 개인 트렌드 분석 가능 여부
    team_trend?: boolean;         // 팀 트렌드 분석 가능 여부
    team_dashboard?: boolean;     // 팀 대시보드 가능 여부
  };
  team?: {
    team_id: number;
    is_owner: boolean;
    owner_plan: string;
    my_role: 'owner' | 'manager' | 'member';  // 사용자의 팀 역할
  } | null;
  // API 키 사용량 정보 (Team 플랜만)
  api_usage?: {
    total_daily_usage: number;
    total_daily_limit: number;
    key_count: number;
  } | null;
  subscription_ends_at?: string | null;  // 구독 종료 예정일 (해지 시)
  is_cancelling?: boolean;  // 해지 예정 여부
}

// ===== Coupon Types =====
export interface RedeemCouponResponse {
  code: string;
  applied_plan?: string | null;
  applied_days?: number | null;
  subscription_ends_at?: string | null;
}

export interface RegisterCouponResponse {
  code: string;
  registered: boolean;
}

export interface RegisteredCouponItem {
  code: string;
  plan: 'free' | 'pro' | 'max' | 'team';
  days: number;
  starts_at?: string | null;
  expires_at?: string | null;
  required_plan?: string | null;
  is_active: boolean;
  registered_at: string;
  used_at?: string | null;
  usable: boolean;
  unusable_code?: string | null;  // 에러 코드 (i18n용)
  unusable_reason?: string | null;  // fallback 메시지
}

export interface RegisteredCouponsResponse {
  items: RegisteredCouponItem[];
}

// ===== History Types =====
export interface HistoryItem {
  id: number;
  feature_name: string;
  release_date: string;
  confidence_score: number | null;
  status: string | null;
  created_at: string;
  folder_id?: number | null;  // 폴더 ID
  // 팀 히스토리용 생성자 정보
  user_id?: number | null;
  user_name?: string | null;
  user_email?: string | null;
}

export interface HistoryResponse {
  items: HistoryItem[];
  total: number;
  limit: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// 토큰 가져오기 헬퍼
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

function notifyAuthInvalid(reason?: string) {
  try {
    // AuthContext와 동일 키
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  } catch {
    // ignore
  }
  try {
    window.dispatchEvent(new CustomEvent('releasegate:auth_invalid', { detail: { reason } }));
  } catch {
    // ignore
  }
}

// 인증 헤더 추가 헬퍼
function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function evaluateRelease(input: ReleaseInput): Promise<ReleaseDecision> {
  try {
    const response = await fetch(`${API_BASE}/api/evaluate`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const defaultMsg = input.lang === 'en' ? 'An error occurred during evaluation' : '평가 중 오류가 발생했습니다';
      const error = new Error(errorData.detail || defaultMsg);
      captureApiError(error, {
        endpoint: '/api/evaluate',
        method: 'POST',
        extra: { 
          status: response.status,
          featureName: input.feature_name,
          hasTestCase: !!input.testcase_file,
        },
      });
      throw error;
    }

    return response.json();
  } catch (error) {
    // 네트워크 에러 등
    if (error instanceof TypeError) {
      captureApiError(error, {
        endpoint: '/api/evaluate',
        method: 'POST',
        extra: { errorType: 'network' },
      });
    }
    throw error;
  }
}

export async function evaluateReleaseMock(input: ReleaseInput): Promise<ReleaseDecision> {
  const response = await fetch(`${API_BASE}/api/evaluate/mock`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    const defaultMsg = input.lang === 'en' ? 'An error occurred during evaluation' : '평가 중 오류가 발생했습니다';
    throw new Error(error.detail || defaultMsg);
  }

  return response.json();
}

// ===== Suggest Testcases API =====

export interface SuggestTestcasesRequest {
  feature_name: string;
  planning_intent: string[];
  risks: string[];
  lang: 'ko' | 'en';
  focus_areas?: string[];  // 집중할 영역 ('core' | 'edge')
  // GitHub 커밋 정보 (선택적)
  github_commits?: {
    sha: string;
    message: string;
    author?: string | null;
    files?: string[];  // 변경된 파일 경로들
  }[];
}

export interface SuggestTestcasesResponse {
  testcases: SuggestedTestCase[];
}

export async function suggestTestcases(request: SuggestTestcasesRequest): Promise<SuggestTestcasesResponse> {
  const response = await fetch(`${API_BASE}/api/suggest-testcases`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    const defaultMsg = request.lang === 'en' ? 'Failed to generate test cases' : '테스트 케이스 생성에 실패했습니다';
    throw new Error(error.detail || defaultMsg);
  }

  return response.json();
}

// ===== Analyze Planning File API =====

export interface ScreenshotAnalysis {
  suggested_feature_name?: string | null;
  ui_components: string[];
  user_flow: string[];
  kpi_suggestions: string[];
}

export interface AnalyzePlanningRequest {
  file_content: string;  // base64
  file_name: string;
  file_type: 'image' | 'pdf' | 'text';
  feature_name?: string;
  lang: 'ko' | 'en';
}

export interface AnalyzePlanningResponse {
  screenshot_analysis?: ScreenshotAnalysis | null;
  extracted_items: string[];
  raw_text?: string | null;
}

export async function analyzePlanningFile(request: AnalyzePlanningRequest): Promise<AnalyzePlanningResponse> {
  const response = await fetch(`${API_BASE}/api/analyze-planning`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    const defaultMsg = request.lang === 'en' ? 'Failed to analyze planning file' : '기획서 분석에 실패했습니다';
    throw new Error(error.detail || defaultMsg);
  }

  return response.json();
}

// ===== Saved Test Cases API =====

export interface TestCaseCategory {
  id: number;
  name: string;
  description?: string;
  color?: string;
  order_index: number;
  testcase_count: number;
}

export interface SavedTestCaseResponse {
  id: number;
  category_id?: number;
  category_name?: string;
  original_id: string;
  test_category: string;
  title: string;
  description: string;
  priority: string;
  source?: string;
  feature_name?: string;
  created_at: string;
}

export interface SaveTestCaseRequest {
  category_id?: number;
  original_id: string;
  test_category: string;
  title: string;
  description: string;
  priority: string;
  source?: string;
  feature_name?: string;
}

export async function getTestCaseCategories(): Promise<TestCaseCategory[]> {
  const response = await fetch(`${API_BASE}/api/testcase-categories`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch categories');
  }

  return response.json();
}

export async function createTestCaseCategory(
  name: string,
  description?: string,
  color?: string
): Promise<TestCaseCategory> {
  const response = await fetch(`${API_BASE}/api/testcase-categories`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name, description, color }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create category');
  }

  return response.json();
}

export async function deleteTestCaseCategory(categoryId: number): Promise<void> {
  const response = await fetch(`${API_BASE}/api/testcase-categories/${categoryId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete category');
  }
}

export async function getSavedTestCases(categoryId?: number): Promise<SavedTestCaseResponse[]> {
  const url = categoryId
    ? `${API_BASE}/api/saved-testcases?category_id=${categoryId}`
    : `${API_BASE}/api/saved-testcases`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch saved test cases');
  }

  return response.json();
}

// 테스트케이스 실패 분석 API
export interface FailureAnalyticsResponse {
  total_evaluations: number;
  total_testcases: number;
  total_failures: number;
  failure_rate: number;
  by_feature: {
    feature_name: string;
    failure_count: number;
    recent_failures: {
      title: string;
      category: string;
      priority: string;
      memo?: string;
      evaluated_at?: string;
    }[];
  }[];
  by_category: Record<string, number>;
  by_priority: { high: number; medium: number; low: number };
  recent_failures: {
    feature_name: string;
    title: string;
    description: string;
    category: string;
    priority: string;
    memo?: string;
    evaluated_at?: string;
  }[];
}

export async function getTestcaseFailureAnalytics(): Promise<FailureAnalyticsResponse> {
  const response = await fetch(`${API_BASE}/api/testcase-failure-analytics`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch failure analytics');
  }

  return response.json();
}

export async function saveTestCase(request: SaveTestCaseRequest): Promise<SavedTestCaseResponse> {
  const response = await fetch(`${API_BASE}/api/saved-testcases`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save test case');
  }

  return response.json();
}

export async function saveTestCasesBulk(testcases: SaveTestCaseRequest[]): Promise<SavedTestCaseResponse[]> {
  const response = await fetch(`${API_BASE}/api/saved-testcases/bulk`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ testcases }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save test cases');
  }

  return response.json();
}

export async function deleteSavedTestCase(testcaseId: number): Promise<void> {
  const response = await fetch(`${API_BASE}/api/saved-testcases/${testcaseId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete test case');
  }
}

// ===== Quota API =====
export async function getQuotaInfo(): Promise<QuotaInfo> {
  const response = await fetch(`${API_BASE}/api/quota`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch quota info');
  }

  return response.json();
}

export async function redeemCoupon(code: string): Promise<RedeemCouponResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/coupons/redeem`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || '쿠폰 적용에 실패했습니다');
      captureApiError(error, {
        endpoint: '/api/coupons/redeem',
        method: 'POST',
        extra: { status: response.status },
      });
      throw error;
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      captureApiError(error, { endpoint: '/api/coupons/redeem', method: 'POST' });
    }
    throw error;
  }
}

export async function registerCoupon(code: string): Promise<RegisterCouponResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/coupons/register`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || '쿠폰 등록에 실패했습니다');
      captureApiError(error, {
        endpoint: '/api/coupons/register',
        method: 'POST',
        extra: { status: response.status },
      });
      throw error;
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      captureApiError(error, { endpoint: '/api/coupons/register', method: 'POST' });
    }
    throw error;
  }
}

export async function listRegisteredCoupons(): Promise<RegisteredCouponsResponse> {
  try {
    const response = await fetch(`${API_BASE}/api/coupons/registered`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.detail || '등록된 쿠폰을 불러오지 못했습니다');
      captureApiError(error, {
        endpoint: '/api/coupons/registered',
        method: 'GET',
        extra: { status: response.status },
      });
      throw error;
    }

    return response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      captureApiError(error, { endpoint: '/api/coupons/registered', method: 'GET' });
    }
    throw error;
  }
}

// ===== History API =====

// 히스토리 탭 정보
export interface HistoryTabInfo {
  team_id: number | null;  // null이면 개인
  team_name: string;
  count: number;
}

export interface HistoryTabsResponse {
  tabs: HistoryTabInfo[];
}

// 히스토리 탭 목록 조회
export async function getHistoryTabs(): Promise<HistoryTabsResponse> {
  const response = await fetch(`${API_BASE}/api/history/tabs`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) notifyAuthInvalid(error?.detail || 'unauthorized');
    throw new Error(error.detail || 'Failed to fetch history tabs');
  }

  return response.json();
}

// 히스토리 조회 (team_id로 필터링 가능)
export interface HistoryFilters {
  folderId?: number | null | 'uncategorized';
  status?: 'go' | 'conditional' | 'holding' | 'no_go' | null;
  dateFrom?: string | null;  // YYYY-MM-DD
  dateTo?: string | null;    // YYYY-MM-DD
  authorId?: number | null;  // 팀 히스토리용
}

export async function getHistory(
  page: number = 1,
  perPage: number = 5,
  teamId?: number | null,  // undefined면 기본, null이면 개인, 숫자면 해당 팀
  filters?: HistoryFilters  // 필터 옵션
): Promise<HistoryResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  
  // teamId가 숫자이면 팀 ID로 필터, null이면 개인만
  if (teamId !== undefined && teamId !== null) {
    params.append('team_id', String(teamId));
  }
  // teamId가 undefined이면 파라미터 안 보냄 (기존 호환)
  
  // 필터링 파라미터
  if (filters) {
    // folderId 필터링
    if (filters.folderId !== undefined) {
      if (filters.folderId === null || filters.folderId === 'uncategorized') {
        params.append('folder_id', 'null');
      } else {
        params.append('folder_id', String(filters.folderId));
      }
    }
    
    // 결과 필터링
    if (filters.status) {
      params.append('status_filter', filters.status);
    }
    
    // 날짜 필터링
    if (filters.dateFrom) {
      params.append('date_from', filters.dateFrom);
    }
    if (filters.dateTo) {
      params.append('date_to', filters.dateTo);
    }
    
    // 작성자 필터링
    if (filters.authorId !== undefined && filters.authorId !== null) {
      params.append('author_id', String(filters.authorId));
    }
  }
  
  const response = await fetch(`${API_BASE}/api/history?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) notifyAuthInvalid(error?.detail || 'unauthorized');
    throw new Error(error.detail || 'Failed to fetch history');
  }

  return response.json();
}

// ===== Evaluation Summary API =====

export interface EvaluationSummaryResponse {
  total: number;
  this_month: number;
}

export async function getEvaluationSummary(): Promise<EvaluationSummaryResponse> {
  const response = await fetch(`${API_BASE}/api/evaluations/summary`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch evaluation summary');
  }

  return response.json();
}

// 히스토리 상세 응답의 GitHub 컨텍스트 타입
export interface HistoryGithubContext {
  repo_full_name: string;
  commits: { sha: string; message: string; author?: string | null; date?: string | null; html_url?: string | null }[];
  merged: {
    focus_areas: { title: string; severity: 'high' | 'medium' | 'low'; reason: string; related_files: string[] }[];
    side_effects: string[];
    suggested_focus_areas: string[];
  };
  highlights?: {
    important_symbols: {
      symbol: string;
      kind: string;
      path: string;
      reason: string;
      snippet?: { path: string; start_line: number; end_line: number; content: string } | null;
    }[];
  } | null;
}

// 히스토리 상세 응답 타입
export interface HistoryDetailResponse {
  id: number;
  feature_name: string;
  release_date: string;
  confidence_score: number;
  status: ReleaseStatus;
  planning_intent: string[];
  risks: string[];
  qa_status: Record<string, string>;
  suggested_testcases: SuggestedTestCase[];
  result: ReleaseDecision | null;
  github_context?: HistoryGithubContext | null;
  created_at: string;
  created_with_plan: string | null;
  testcase_file_name: string | null;
  has_testcase_file: boolean;
  planning_file_names: string[];
  has_planning_files: boolean;
}

export async function getHistoryDetail(recordId: number): Promise<HistoryDetailResponse> {
  const response = await fetch(`${API_BASE}/api/history/${recordId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch history detail');
  }

  return response.json();
}

// ===== Team History API =====

export interface TeamHistoryItem {
  id: number;
  feature_name: string;
  release_date: string;
  confidence_score: number | null;
  status: string | null;
  created_at: string;
  user_id: number;
  user_name: string | null;
  user_email: string;
}

export interface TeamHistoryResponse {
  items: TeamHistoryItem[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  team_name: string;
}

export async function getTeamHistory(page: number = 1, perPage: number = 10): Promise<TeamHistoryResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  
  const response = await fetch(`${API_BASE}/api/team/history?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch team history');
  }

  return response.json();
}

// ===== Team Dashboard API =====

export interface TeamUserCount {
  user_id: number;
  user_name: string | null;
  user_email: string;
  count: number;
}

export interface TeamStatusDistribution {
  go: number;
  conditional: number;
  holding: number;
  no_go: number;
}

export interface TeamDailyEvaluation {
  date: string; // YYYY-MM-DD
  count: number;
}

export interface TeamDashboardResponse {
  team_name: string;
  period_days: number;
  total_evaluations: number;
  status_distribution: TeamStatusDistribution;
  daily_evaluations: TeamDailyEvaluation[];
  top_users: TeamUserCount[];
}

export async function getTeamDashboard(days: number = 30): Promise<TeamDashboardResponse> {
  const params = new URLSearchParams({ days: String(days) });
  const response = await fetch(`${API_BASE}/api/team/dashboard?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch team dashboard');
  }

  return response.json();
}

export async function getTeamHistoryDetail(recordId: number): Promise<HistoryDetailResponse> {
  const response = await fetch(`${API_BASE}/api/team/history/${recordId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch team history detail');
  }

  return response.json();
}

// ===== Signature API =====

export interface SignatureResponse {
  has_signature: boolean;
  signature_data: string | null;
}

export async function getSignature(): Promise<SignatureResponse> {
  const response = await fetch(`${API_BASE}/api/signature`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch signature');
  }

  return response.json();
}

export async function uploadSignature(signatureData: string): Promise<SignatureResponse> {
  const response = await fetch(`${API_BASE}/api/signature`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ signature_data: signatureData }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to upload signature');
  }

  return response.json();
}

export async function deleteSignature(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/signature`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete signature');
  }

  return response.json();
}

// ===== Statistics API =====

export interface DailyEvaluation {
  date: string;  // YYYY-MM-DD
  count: number;
}

export interface StatusDistribution {
  go: number;
  conditional: number;
  holding: number;
  no_go: number;
}

export interface KeywordCount {
  keyword: string;
  count: number;
}

export interface StatisticsResponse {
  daily_evaluations: DailyEvaluation[];
  status_distribution: StatusDistribution;
  total_evaluations: number;
  average_score: number | null;
  period_days: number;
  top_keywords: KeywordCount[];
}

export async function getStatistics(days: number = 30): Promise<StatisticsResponse> {
  const params = new URLSearchParams({
    days: String(days),
  });
  
  const response = await fetch(`${API_BASE}/api/statistics?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch statistics');
  }

  return response.json();
}

// ===== Payments API =====

export interface CheckoutResponse {
  checkout_url: string;
}

export async function createCheckout(plan: 'pro' | 'max' | 'team'): Promise<CheckoutResponse> {
  const response = await fetch(`${API_BASE}/api/payments/checkout`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ plan }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create checkout');
  }

  return response.json();
}

export async function createExtraSeatsCheckout(teamId: number): Promise<CheckoutResponse> {
  const response = await fetch(`${API_BASE}/api/payments/checkout/extra-seats`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ team_id: teamId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create checkout');
  }

  return response.json();
}

export interface SubscriptionInfo {
  plan: 'free' | 'pro' | 'max' | 'team';
  is_active: boolean;
  subscription_started_at: string | null;
  subscription_ends_at: string | null;
  next_billing_at: string | null;
  billing_period: 'monthly' | 'annually' | null;
  is_cancelling: boolean;
  is_beta: boolean;
}

export async function getSubscription(): Promise<SubscriptionInfo> {
  const response = await fetch(`${API_BASE}/api/payments/subscription`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch subscription');
  }

  return response.json();
}

// ====================================================================
// GitHub Commit Review (Max/Team)
// ====================================================================

export interface GithubCommitItem {
  sha: string;
  message: string;
  author?: string | null;
  date?: string | null;
  html_url?: string | null;
}

export interface GithubCommitsResponse {
  items: GithubCommitItem[];
  page: number;
  per_page: number;
}

export interface GithubCommitFileChange {
  filename: string;
  status?: string | null;
  additions?: number | null;
  deletions?: number | null;
  changes?: number | null;
  patch?: string | null;
}

export interface GithubCommitDetailResponse {
  sha: string;
  message: string;
  author?: string | null;
  date?: string | null;
  html_url?: string | null;
  files: GithubCommitFileChange[];
}

export interface GithubFocusArea {
  title: string;
  severity: 'high' | 'medium' | 'low';
  reason: string;
  related_files: string[];
}

export interface GithubCodeSnippet {
  path: string;
  start_line: number;
  end_line: number;
  content: string;
}

export interface GithubImportantSymbol {
  symbol: string;
  kind: string;
  path: string;
  reason: string;
  snippet?: GithubCodeSnippet | null;
}

export interface GithubCommitAnalysisResponse {
  commit: GithubCommitDetailResponse;
  focus_areas: GithubFocusArea[];
  side_effects: string[];
  important_symbols: GithubImportantSymbol[];
  suggested_focus_areas: string[];
}

export interface GithubRepoItem {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  html_url: string;
  default_branch?: string | null;
  updated_at?: string | null;
}

export interface GithubReposResponse {
  items: GithubRepoItem[];
  page: number;
  per_page: number;
}

export async function getGithubRepos(options?: { q?: string; page?: number; perPage?: number }): Promise<GithubReposResponse> {
  const params = new URLSearchParams({
    page: String(options?.page ?? 1),
    per_page: String(options?.perPage ?? 30),
  });
  const q = (options?.q ?? '').trim();
  if (q) params.append('q', q);

  const response = await fetch(`${API_BASE}/api/integrations/github/repos?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to fetch repositories');
  }
  return response.json();
}

export async function getGithubCommits(
  owner: string,
  repo: string,
  options?: { page?: number; perPage?: number; sha?: string }
): Promise<GithubCommitsResponse> {
  const params = new URLSearchParams({
    page: String(options?.page ?? 1),
    per_page: String(options?.perPage ?? 100),
  });
  if (options?.sha) params.append('sha', options.sha);

  const response = await fetch(`${API_BASE}/api/integrations/github/repo/${owner}/${repo}/commits?${params}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to fetch commits');
  }
  return response.json();
}

export async function analyzeGithubCommit(
  owner: string,
  repo: string,
  sha: string,
  options?: { maxRelatedFiles?: number; maxSymbols?: number }
): Promise<GithubCommitAnalysisResponse> {
  const params = new URLSearchParams();
  if (options?.maxRelatedFiles !== undefined) params.append('max_related_files', String(options.maxRelatedFiles));
  if (options?.maxSymbols !== undefined) params.append('max_symbols', String(options.maxSymbols));

  const qs = params.toString() ? `?${params}` : '';
  const response = await fetch(`${API_BASE}/api/integrations/github/repo/${owner}/${repo}/commit/${sha}/analyze${qs}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to analyze commit');
  }
  return response.json();
}

// ====================================================================
// GitHub Alignment (Evidence-based)
// ====================================================================

export interface GithubAlignmentEvidence {
  source: string;
  quote: string;
}

export interface GithubAlignmentAlignedItem {
  item: string;
  reason: string;
  evidence: GithubAlignmentEvidence[];
}

export interface GithubAlignmentGapItem {
  item: string;
  why_gap: string;
  suggested_additions?: string[] | null;
  suggested_testcases?: string[] | null;
}

export interface GithubAlignmentSection {
  score: number;
  aligned: GithubAlignmentAlignedItem[];
  gaps: GithubAlignmentGapItem[];
}

export interface GithubTopSideEffect {
  effect: string;
  severity: 'high' | 'medium' | 'low' | string;
  why: string;
  evidence: GithubAlignmentEvidence[];
}

export interface GithubAlignmentSummary {
  top_side_effects: GithubTopSideEffect[];
  overall_comment: string;
}

export interface GithubAlignmentRequest {
  github: {
    repo_full_name: string;
    commits: GithubCommitItem[];
    merged: {
      focus_areas: GithubFocusArea[];
      side_effects: string[];
      suggested_focus_areas: string[];
    };
  };
  planning_intent: string[];
  risks: string[];
  suggested_testcases?: SuggestedTestCase[] | null;
  lang: 'ko' | 'en';
}

export interface GithubAlignmentResponse {
  planning_risk_alignment: GithubAlignmentSection;
  testcase_alignment: GithubAlignmentSection;
  summary: GithubAlignmentSummary;
}

export async function evaluateGithubAlignment(request: GithubAlignmentRequest): Promise<GithubAlignmentResponse> {
  const response = await fetch(`${API_BASE}/api/github-alignment`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to evaluate GitHub alignment');
  }
  return response.json();
}

// ===== 로컬 개발 환경 전용 테스트 API =====

export interface DevSubscribeResponse {
  success: boolean;
  message: string;
  previous_plan: string;
  new_plan: string;
}

/**
 * [로컬 환경 전용] 결제 없이 구독 플랜 변경
 * 
 * ⚠️ staging/production 환경에서는 403 에러 반환
 */
export async function devSubscribe(plan: 'free' | 'pro' | 'max' | 'team'): Promise<DevSubscribeResponse> {
  console.log(`[API] devSubscribe 요청: plan=${plan}`);
  
  const response = await fetch(`${API_BASE}/api/payments/dev/subscribe`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ plan }),
  });

  if (!response.ok) {
    const error = await response.json();
    console.error(`[API] devSubscribe 실패:`, error);
    throw new Error(error.detail || 'Failed to change subscription');
  }
  
  const result = await response.json();
  console.log(`[API] devSubscribe 성공:`, result);
  return result;
}

export interface DevCancelResponse {
  success: boolean;
  message: string;
  subscription_ends_at: string;
  current_plan?: string;
}

/**
 * [로컬 환경 전용] 구독 해지 (월말까지 사용 가능)
 * 
 * ⚠️ staging/production 환경에서는 403 에러 반환
 */
export async function devCancelSubscription(): Promise<DevCancelResponse> {
  const response = await fetch(`${API_BASE}/api/payments/dev/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to cancel subscription');
  }

  return response.json();
}

export interface DevReactivateResponse {
  success: boolean;
  message: string;
  current_plan: string;
}

/**
 * [로컬 환경 전용] 해지 예정 취소 (구독 재활성화)
 * 
 * ⚠️ staging/production 환경에서는 403 에러 반환
 */
export async function devReactivateSubscription(): Promise<DevReactivateResponse> {
  const response = await fetch(`${API_BASE}/api/payments/dev/reactivate`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reactivate subscription');
  }

  return response.json();
}

export interface DevExpireResponse {
  success: boolean;
  message: string;
  previous_plan: string;
  new_plan: string;
  team_deleted?: boolean;
  team_name?: string;
  members_affected?: number;
}

/**
 * [로컬 환경 전용] 구독 즉시 만료 (팀 삭제 테스트용)
 * 
 * ⚠️ staging/production 환경에서는 403 에러 반환
 * 팀 오너인 경우 팀이 삭제됩니다.
 */
export async function devExpireSubscription(): Promise<DevExpireResponse> {
  const response = await fetch(`${API_BASE}/api/payments/dev/expire`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to expire subscription');
  }

  return response.json();
}

// ===== PDF Report API =====

export interface PDFReportRequest {
  result: Record<string, unknown>;
  input_data?: Record<string, unknown>;
  github_context?: Record<string, unknown> | null;
  lang: string;
}

export async function downloadPdfReport(
  result: Record<string, unknown>,
  inputData?: Record<string, unknown>,
  lang: string = 'ko',
  githubContext?: Record<string, unknown> | null
): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/report/pdf`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      result,
      input_data: inputData,
      github_context: githubContext ?? null,
      lang,
    } as PDFReportRequest),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) notifyAuthInvalid(error?.detail || 'unauthorized');
    throw new Error(error.detail || 'Failed to generate PDF');
  }

  return response.blob();
}

export async function downloadHistoryPdf(
  recordId: number | null | undefined,
  lang: string = 'ko'
): Promise<Blob> {
  // recordId 검증
  if (!recordId || typeof recordId !== 'number') {
    throw new Error('Invalid record ID');
  }
  
  const response = await fetch(`${API_BASE}/api/report/pdf/${recordId}?lang=${lang}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    if (response.status === 401) notifyAuthInvalid(error?.detail || 'unauthorized');
    throw new Error(error.detail || 'Failed to download PDF');
  }

  return response.blob();
}

export async function downloadTestcaseFile(
  recordId: number
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API_BASE}/api/history/${recordId}/testcase-file`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to download testcase file');
  }

  // Content-Disposition 헤더에서 파일명 추출
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'testcase_file';
  if (contentDisposition) {
    // filename*=UTF-8''encoded_name 형식 파싱
    const match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
    if (match) {
      filename = decodeURIComponent(match[1]);
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

export async function downloadPlanningFile(
  recordId: number,
  fileIndex: number
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API_BASE}/api/history/${recordId}/planning-file/${fileIndex}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to download planning file');
  }

  // Content-Disposition 헤더에서 파일명 추출
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `planning_file_${fileIndex}`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
    if (match) {
      filename = decodeURIComponent(match[1]);
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

export async function downloadAllFilesZip(
  recordId: number
): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`${API_BASE}/api/history/${recordId}/files-zip`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to download files');
  }

  // Content-Disposition 헤더에서 파일명 추출
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `files_${recordId}.zip`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
    if (match) {
      filename = decodeURIComponent(match[1]);
    }
  }

  const blob = await response.blob();
  return { blob, filename };
}

// ===== Team API Types =====

export interface TeamMember {
  id: number;
  user_id: number;
  email: string;
  name: string | null;
  picture: string | null;
  role: 'owner' | 'manager' | 'member';
  joined_at: string;
}

export interface TeamInvite {
  id: number;
  email: string;
  role: string;
  invited_by_email: string | null;
  created_at: string;
  expires_at: string;
  is_expired: boolean;
}

export interface TeamInfo {
  id: number;
  name: string;
  owner_id: number;
  max_members: number;
  member_count: number;
  created_at: string;
  is_frozen: boolean;  // 팀 오너의 구독이 Team이 아니면 frozen
}

export interface TeamDetailResponse {
  team: TeamInfo;
  members: TeamMember[];
  pending_invites: TeamInvite[];
}

export interface InviteInfoResponse {
  team_name: string;
  invited_by: string | null;
  role: string;
  expires_at: string;
  is_expired: boolean;
}

// ===== Team API =====

export async function createTeam(name: string): Promise<TeamInfo> {
  const response = await fetch(`${API_BASE}/api/team`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create team');
  }

  return response.json();
}

export async function getTeam(): Promise<TeamDetailResponse | null> {
  const response = await fetch(`${API_BASE}/api/team`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch team');
  }

  return response.json();
}

export async function inviteTeamMember(
  email: string,
  role: 'manager' | 'member' = 'member'
): Promise<TeamInvite> {
  const response = await fetch(`${API_BASE}/api/team/invite`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to invite member');
  }

  return response.json();
}

export async function getInviteInfo(token: string): Promise<InviteInfoResponse> {
  const response = await fetch(`${API_BASE}/api/team/invite/${token}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Invalid invite');
  }

  return response.json();
}

export async function joinTeam(token: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/team/join/${token}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to join team');
  }

  return response.json();
}

export async function removeTeamMember(memberId: number): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/team/members/${memberId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to remove member');
  }

  return response.json();
}

export async function cancelInvite(inviteId: number): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/team/invites/${inviteId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to cancel invite');
  }

  return response.json();
}

export async function resendInvite(inviteId: number): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/team/invites/${inviteId}/resend`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to resend invite');
  }

  return response.json();
}

export async function updateMemberRole(
  memberId: number,
  role: 'manager' | 'member'
): Promise<TeamMember> {
  const response = await fetch(`${API_BASE}/api/team/members/${memberId}/role`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ role }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update role');
  }

  return response.json();
}

export async function leaveTeam(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/team/leave`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to leave team');
  }

  return response.json();
}

export async function deleteTeam(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/team`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete team');
  }

  return response.json();
}

export interface TransferOwnershipResponse {
  message: string;
  new_owner_id: number;
  new_owner_name: string | null;
  new_owner_email: string;
}

export async function transferOwnership(newOwnerId: number): Promise<TransferOwnershipResponse> {
  const response = await fetch(`${API_BASE}/api/team/transfer-ownership`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ new_owner_id: newOwnerId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to transfer ownership');
  }

  return response.json();
}

// ===== API Keys Types =====

export interface ApiKeyInfo {
  id: number;
  name: string;
  key_prefix: string;
  daily_usage: number;
  daily_limit: number;
  last_used_at: string | null;
  created_at: string;
  is_revoked: boolean;
}

export interface ApiKeyListResponse {
  keys: ApiKeyInfo[];
  max_keys: number;
  // 총 사용량 요약 (팀 전체)
  total_daily_usage: number;
  total_daily_limit: number;
}

export interface CreateApiKeyResponse {
  id: number;
  name: string;
  key: string;  // 전체 키 (한 번만 표시!)
  key_prefix: string;
  created_at: string;
}

// ===== API Keys API =====

export async function createApiKey(name: string): Promise<CreateApiKeyResponse> {
  const response = await fetch(`${API_BASE}/api/keys`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create API key');
  }

  return response.json();
}

export async function listApiKeys(): Promise<ApiKeyListResponse> {
  const response = await fetch(`${API_BASE}/api/keys`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to list API keys');
  }

  return response.json();
}

export async function revokeApiKey(keyId: number): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/keys/${keyId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to revoke API key');
  }

  return response.json();
}

// ===== Folder API Types =====

export interface FolderInfo {
  id: number;
  name: string;
  parent_id: number | null;
  team_id: number | null;
  sort_order: number;
  children: FolderInfo[];
  item_count: number;
}

export interface FoldersTreeResponse {
  folders: FolderInfo[];
  uncategorized_count: number;
}

export interface FolderCreateRequest {
  name: string;
  parent_id?: number | null;
  team_id?: number | null;
}

export interface FolderUpdateRequest {
  name?: string;
  parent_id?: number | null;
}

export interface HistoryUpdateRequest {
  feature_name?: string;
  folder_id?: number | null;  // 0이면 분류 해제
}

// ===== Folder API =====

export async function getFoldersTree(teamId?: number | null): Promise<FoldersTreeResponse> {
  const params = new URLSearchParams();
  if (teamId !== undefined && teamId !== null) {
    params.append('team_id', String(teamId));
  }
  
  const url = params.toString() 
    ? `${API_BASE}/api/folders?${params}` 
    : `${API_BASE}/api/folders`;
    
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch folders');
  }

  return response.json();
}

export async function createFolder(data: FolderCreateRequest): Promise<FolderInfo> {
  const response = await fetch(`${API_BASE}/api/folders`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create folder');
  }

  return response.json();
}

export async function updateFolder(folderId: number, data: FolderUpdateRequest): Promise<FolderInfo> {
  const response = await fetch(`${API_BASE}/api/folders/${folderId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update folder');
  }

  return response.json();
}

export async function deleteFolder(folderId: number): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/folders/${folderId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete folder');
  }

  return response.json();
}

export async function updateHistory(
  recordId: number, 
  data: HistoryUpdateRequest
): Promise<{ id: number; feature_name: string; folder_id: number | null; message: string }> {
  const response = await fetch(`${API_BASE}/api/history/${recordId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update history');
  }

  return response.json();
}

// ===== Release Criteria Types (Max 플랜 전용) =====

export type BlockerType = 'payment' | 'personal_info' | 'auth' | 'webhook' | 'delete';

export type DomainType = 'fintech' | 'ecommerce' | 'healthcare' | 'social' | 'saas' | 'gaming' | 'media' | 'education' | 'other';

export interface CustomBlocker {
  keyword: string;
  message: string;
}

export interface AdditionalRules {
  test_fail_downgrades_go: boolean;
  nfr_missing_forces_holding: boolean;
  ac_zero_forces_nogo: boolean;
}

export interface ReleaseCriteriaInput {
  domain?: DomainType;
  go_threshold: number;
  conditional_threshold: number;
  holding_threshold: number;
  enabled_blockers: BlockerType[];
  custom_blockers: CustomBlocker[];
  additional_rules: AdditionalRules;
}

export interface ReleaseCriteria {
  id: number;
  user_id: number;
  domain: DomainType | null;
  go_threshold: number;
  conditional_threshold: number;
  holding_threshold: number;
  enabled_blockers: BlockerType[];
  custom_blockers: CustomBlocker[];
  additional_rules: AdditionalRules;
  created_at: string;
  updated_at: string;
}

export interface DomainRecommendation {
  domain: DomainType;
  recommended_blockers: BlockerType[];
  recommended_go_threshold: number;
  recommended_rules: AdditionalRules;
  reason: string;
}

export interface BlockerInfo {
  type: BlockerType;
  name: string;
  name_en: string;
  description: string;
  keywords: string[];
}

// ===== Release Criteria API Functions =====

export async function getReleaseCriteria(): Promise<ReleaseCriteria | null> {
  const response = await fetch(`${API_BASE}/api/criteria`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Max 플랜 이상에서만 사용 가능합니다.');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch criteria');
  }

  const data = await response.json();
  return data;
}

export async function saveReleaseCriteria(input: ReleaseCriteriaInput): Promise<ReleaseCriteria> {
  const response = await fetch(`${API_BASE}/api/criteria`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Max 플랜 이상에서만 사용 가능합니다.');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to save criteria');
  }

  return response.json();
}

export async function deleteReleaseCriteria(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/api/criteria`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Max 플랜 이상에서만 사용 가능합니다.');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete criteria');
  }

  return response.json();
}

export async function getDomainRecommendation(domain: DomainType): Promise<DomainRecommendation> {
  const response = await fetch(`${API_BASE}/api/criteria/recommend/${domain}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Max 플랜 이상에서만 사용 가능합니다.');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch recommendation');
  }

  return response.json();
}

export async function getAvailableBlockers(): Promise<{ blockers: BlockerInfo[] }> {
  const response = await fetch(`${API_BASE}/api/criteria/blockers`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new Error('Max 플랜 이상에서만 사용 가능합니다.');
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch blockers');
  }

  return response.json();
}

// ====================================================================
// 소프트웨어 뉴스 API
// ====================================================================

export interface SoftwareNewsItem {
  id: number;
  title: string;
  summary: string;
  source_url: string | null;
  source_name: string | null;
  time_slot: 'morning' | 'noon' | 'evening' | 'night';
  publish_date: string;
  category: string;
}

/**
 * 현재 시간대에 맞는 최신 뉴스 가져오기
 */
export async function getCurrentNews(lang: string = 'ko'): Promise<SoftwareNewsItem | null> {
  try {
    const response = await fetch(`${API_BASE}/api/news/current`, {
      method: 'GET',
      headers: {
        'Accept-Language': lang,
      },
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data || null;
  } catch {
    return null;
  }
}

/**
 * 최근 N일간의 뉴스 목록 가져오기
 */
export async function getNewsList(days: number = 7, lang: string = 'ko'): Promise<SoftwareNewsItem[]> {
  try {
    const response = await fetch(`${API_BASE}/api/news?days=${days}`, {
      method: 'GET',
      headers: {
        'Accept-Language': lang,
      },
    });

    if (!response.ok) {
      return [];
    }

    return response.json();
  } catch {
    return [];
  }
}

// ====================================================================
// Flow Testcase Generator API (화면 흐름 기반 테스트케이스 자동 생성)
// ====================================================================

export interface FlowScreenInput {
  id: string;
  name: string;
  is_menu?: boolean;
  parent_id?: string | null;
  url_pattern?: string | null;
  screenshot_base64?: string | null;
}

export interface FlowTransitionInput {
  from_screen: string;
  to_screen: string;
  trigger_label: string;
  trigger_testid?: string | null;
  trigger_selector?: string | null;
  condition?: string | null;
}

export interface FlowAssertionInput {
  screen_id: string;
  assertion_type?: string;
  selector_testid?: string | null;
  selector_css?: string | null;
  expected_text?: string | null;
  expected_url?: string | null;
}

export interface FlowContextInput {
  id: string;
  name: string;
  conditions?: Record<string, string>;
  is_default?: boolean;
}

export interface FlowTestcaseGeneratorInput {
  screens: FlowScreenInput[];
  transitions: FlowTransitionInput[];
  assertions?: FlowAssertionInput[] | null;
  contexts?: FlowContextInput[] | null;
  testid_prefix?: string;
  auto_generate_testids?: boolean;
  auto_generate_assertions?: boolean;
  analyze_screenshots?: boolean;
  lang?: 'ko' | 'en';
}

export interface FlowCSVOutput {
  screens_csv: string;
  transitions_csv: string;
  menu_nodes_csv: string;
  assertions_csv: string;
  contexts_csv: string;
  testcases_csv: string;
}

export interface FlowTestcaseGeneratorResponse {
  success: boolean;
  csv_outputs: FlowCSVOutput;
  stats: Record<string, unknown>;
  warnings: string[];
  vision_analysis?: Record<string, unknown>[] | null;
}

/**
 * 화면 흐름 기반 테스트케이스 CSV 생성
 */
export async function generateFlowCSV(
  input: FlowTestcaseGeneratorInput
): Promise<FlowTestcaseGeneratorResponse> {
  const response = await fetch(`${API_BASE}/api/generate-flow-csv`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate flow CSV');
  }

  return response.json();
}

/**
 * 화면 흐름 기반 테스트케이스 CSV를 ZIP으로 다운로드
 */
export async function downloadFlowCSVZip(
  input: FlowTestcaseGeneratorInput
): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/generate-flow-csv/download`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to download flow CSV');
  }

  return response.blob();
}

